/**
 * V2 sync (Theme 10, from 7cc48382).
 *
 * `<TelemetryBridge />` — invisible component that wires the SPA's
 * Phase 19-14 telemetry plumbing:
 *
 *   1. Installs `window.onerror` + `unhandledrejection` handlers ONCE,
 *      reading the live auth state via refs so logout/login switches
 *      take effect immediately.
 *   2. Renders the one-time toast notice for tester / super-admin
 *      sessions (via `useTelemetryNotice`).
 *
 * Mount this once near the top of the app tree (inside the auth +
 * QueryClient providers, before any module routes). It returns null —
 * its job is purely side-effect installation.
 *
 * Why a component instead of just calling install in `main.tsx`:
 * the install handlers need to read the *current* auth state on every
 * fired event, not the value at install time. React refs give us that
 * cheaply without a global mutable singleton.
 */
import { useEffect, useRef } from 'react'
import { useAuth } from '@/lib/auth'
import { installGlobalTelemetryHandlers } from './installGlobalHandlers'
import { useTelemetryNotice } from './useTelemetryNotice'

export function TelemetryBridge(): null {
  const { authStatus } = useAuth()

  // Refs hold the latest auth values so the global handlers (installed
  // once on mount) always see fresh state. Without refs, the install
  // would close over the initial render's authStatus and stay stale
  // across login/logout — a real footgun that takes weeks to notice.
  const telemetryEnabledRef = useRef<boolean>(false)
  const tokenRef = useRef<string | null>(null)

  useEffect(() => {
    telemetryEnabledRef.current = Boolean(authStatus?.user?.telemetryEnabled)
    tokenRef.current = authStatus?.token ?? null
  }, [authStatus])

  useEffect(() => {
    installGlobalTelemetryHandlers({
      getTelemetryEnabled: () => telemetryEnabledRef.current,
      getToken: () => tokenRef.current,
      source: 'spa',
    })
    // Install is idempotent — safe to call again on re-mount even
    // though StrictMode will exercise that path in dev.
  }, [])

  useTelemetryNotice()

  return null
}
